// ------------------------------------------------------------
// GA4 計測（公式サイト連携 H4 / 詳細設計書 v1.1 §13）
//   - 測定ID は環境変数 VITE_GA_MEASUREMENT_ID。未設定なら何もしない（開発・テスト環境）
//   - ゲスト向け画面のみ。/admin /cleaner /passport /checkin では読み込まない
//   - Consent Mode v2：既定は denied（Cookie を使わない計測）。
//     localStorage 'sd_consent' === 'granted' のときだけ analytics_storage を granted にする
//   - 個人情報（氏名・メール・電話・住所・パスポート・トークン）は送らない。
//     page_location からクエリを除去して送る（/booking/complete?email=... 対策）
// ------------------------------------------------------------
declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const GA_ID = (import.meta as any).env?.VITE_GA_MEASUREMENT_ID as string | undefined;
const EXCLUDED = [/^\/admin(\/|$)/, /^\/cleaner(\/|$)/, /^\/passport(\/|$)/, /^\/checkin(\/|$)/];
// 送ってよいクエリ（それ以外は除去）
const ALLOWED_QUERY = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'lng'];

let loaded = false;

function isExcluded(path: string): boolean {
  return EXCLUDED.some((re) => re.test(path));
}

function enabled(): boolean {
  return !!GA_ID && typeof window !== 'undefined' && !isExcluded(window.location.pathname);
}

/** URL からトークン・メール等を除いた page_location を作る */
export function sanitizedLocation(href = window.location.href): string {
  try {
    const u = new URL(href);
    const kept = new URLSearchParams();
    for (const k of ALLOWED_QUERY) {
      const v = u.searchParams.get(k);
      if (v) kept.set(k, v);
    }
    const q = kept.toString();
    return `${u.origin}${u.pathname}${q ? `?${q}` : ''}`;
  } catch {
    return '';
  }
}

function ensureLoaded(): boolean {
  if (!enabled()) return false;
  if (loaded) return true;
  loaded = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  let granted = false;
  try { granted = localStorage.getItem('sd_consent') === 'granted'; } catch { /* noop */ }
  window.gtag('consent', 'default', {
    analytics_storage: granted ? 'granted' : 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  window.gtag('js', new Date());
  window.gtag('config', GA_ID, {
    send_page_view: false, // SPA のため page_view は手動送信（URL を除去してから）
    // 公式サイト（staydreamhotel.com）と同一プロパティ。サブドメイン間で Cookie 共有
    cookie_domain: 'auto',
    linker: { domains: ['staydreamhotel.com', 'booking.staydreamhotel.com'] },
  });
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID!)}`;
  document.head.appendChild(s);
  return true;
}

function send(name: string, params: Record<string, unknown>) {
  if (!ensureLoaded() || !window.gtag) return;
  window.gtag('event', name, params);
}

export function trackPageView(lang: string) {
  send('page_view', {
    page_location: sanitizedLocation(),
    page_path: window.location.pathname,
    page_title: document.title,
    lang,
  });
}

/** ゲスト情報入力画面の表示時 */
export function trackBeginCheckout(p: {
  propertyId: string; propertyName?: string; value: number; currency: string; lang: string; nights?: number; guests?: number;
}) {
  send('begin_checkout', {
    currency: p.currency,
    value: p.value,
    property_id: p.propertyId,
    lang: p.lang,
    nights: p.nights,
    guests: p.guests,
    items: [{ item_id: p.propertyId, item_name: p.propertyName, quantity: 1, price: p.value }],
  });
}

/**
 * 予約確定時に1回だけ送る。
 *   - 同じ予約IDは端末内で再送しない（再読込・戻る対策）。GA4 側も transaction_id で重複排除される
 *   - 予約ID は UUID（推測不能）なので transaction_id に使ってよい
 * @returns 実際に送信したら true
 */
export function trackPurchaseOnce(p: {
  reservationId: string; propertyId: string; propertyName?: string; value: number; currency: string;
  lang: string; coupon?: string | null;
}): boolean {
  const key = `sd_ga_purchase_${p.reservationId}`;
  try {
    if (localStorage.getItem(key)) return false;
  } catch { /* 保存できない環境でも送信は行う（GA4 側の重複排除に任せる） */ }
  if (!enabled()) return false;
  send('purchase', {
    transaction_id: p.reservationId,
    currency: p.currency,
    value: p.value,
    coupon: p.coupon || undefined,
    property_id: p.propertyId,
    lang: p.lang,
    items: [{ item_id: p.propertyId, item_name: p.propertyName, quantity: 1, price: p.value }],
  });
  try { localStorage.setItem(key, new Date().toISOString()); } catch { /* noop */ }
  return true;
}
